/**
 * Durable local picture media.
 *
 * Time and materials stay in the existing localStorage queue. Image bytes
 * cannot live there, so Blobs go in IndexedDB and share CrewPilot's sync
 * status. Metadata reads never load original Blobs.
 *
 * Sign-out does not delete these records. Another account does not receive
 * them because every read is scoped to organization and creator.
 */

const DB_NAME = "crewpilot-sync";
const DB_VERSION = 2;
const META = "pictureMeta";
const BLOBS = "pictureBlobs";
const THUMBS = "pictureThumbs";
const NOTES = "pictureNotes";
const LIBRARY = "pictureLibrary";
const LIBRARY_THUMBS = "libraryThumbs";
const COMMENT_CACHE = "pictureCommentCache";

const listeners = new Set();
let dbPromise = null;
let memory = null;
let forceMemory = false;
let putFailure = null;
let draining = false;

function notify(event) {
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // A status listener cannot block the queue.
    }
  });
}

function emptyMemory() {
  return {
    meta: new Map(),
    blobs: new Map(),
    thumbs: new Map(),
    notes: new Map(),
    library: new Map(),
    libraryThumbs: new Map(),
    commentCache: new Map(),
  };
}

function storesInMemory() {
  return forceMemory || typeof indexedDB === "undefined" || !indexedDB;
}

function memoryDb() {
  if (!memory) memory = emptyMemory();
  return memory;
}

function openDb() {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META, { keyPath: "picture_id" });
        if (!db.objectStoreNames.contains(BLOBS)) db.createObjectStore(BLOBS);
        if (!db.objectStoreNames.contains(THUMBS)) db.createObjectStore(THUMBS);
        if (!db.objectStoreNames.contains(NOTES)) db.createObjectStore(NOTES, { keyPath: "comment_id" });
        if (!db.objectStoreNames.contains(LIBRARY)) db.createObjectStore(LIBRARY, { keyPath: "cache_key" });
        if (!db.objectStoreNames.contains(LIBRARY_THUMBS)) db.createObjectStore(LIBRARY_THUMBS);
        if (!db.objectStoreNames.contains(COMMENT_CACHE)) db.createObjectStore(COMMENT_CACHE, { keyPath: "cache_key" });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error || new Error("Picture storage is unavailable."));
    });
  }
  return dbPromise;
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Picture storage failed."));
    tx.onabort = () => reject(tx.error || new Error("Picture storage failed."));
  });
}

export function subscribePictureSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isPictureSyncDraining() {
  return draining;
}

export function setPictureSyncDraining(value) {
  draining = Boolean(value);
  notify({ type: "activity" });
}

export async function putLocalPicture({ meta, blob, thumb }) {
  if (putFailure) {
    const error = putFailure;
    putFailure = null;
    throw error;
  }
  const record = { ...meta };
  if (storesInMemory()) {
    const db = memoryDb();
    db.meta.set(record.picture_id, record);
    if (blob) db.blobs.set(record.picture_id, blob);
    if (thumb) db.thumbs.set(record.picture_id, thumb);
  } else {
    const db = await openDb();
    const tx = db.transaction([META, BLOBS, THUMBS], "readwrite");
    tx.objectStore(META).put(record);
    if (blob) tx.objectStore(BLOBS).put(blob, record.picture_id);
    if (thumb) tx.objectStore(THUMBS).put(thumb, record.picture_id);
    await transactionDone(tx);
  }
  notify({ type: "update", pictureId: record.picture_id });
  return record;
}

export async function updateLocalPicture(pictureId, patch) {
  const current = await getLocalPicture(pictureId);
  if (!current) return null;
  const next = { ...current, ...patch, picture_id: pictureId };
  if (storesInMemory()) {
    memoryDb().meta.set(pictureId, next);
  } else {
    const db = await openDb();
    const tx = db.transaction(META, "readwrite");
    tx.objectStore(META).put(next);
    await transactionDone(tx);
  }
  notify({ type: "update", pictureId });
  return next;
}

export async function getLocalPicture(pictureId) {
  if (!pictureId) return null;
  if (storesInMemory()) {
    const row = memoryDb().meta.get(pictureId);
    return row ? { ...row } : null;
  }
  const db = await openDb();
  const row = await new Promise((resolve, reject) => {
    const tx = db.transaction(META, "readonly");
    const request = tx.objectStore(META).get(pictureId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
  return row ? { ...row } : null;
}

export async function listLocalPictures({ organizationId, createdBy, projectId } = {}) {
  if (!organizationId || !createdBy) return [];
  let rows;
  if (storesInMemory()) {
    rows = [...memoryDb().meta.values()];
  } else {
    const db = await openDb();
    rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(META, "readonly");
      const request = tx.objectStore(META).getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }
  return rows
    .filter((row) => row.organization_id === organizationId && row.created_by === createdBy)
    .filter((row) => !projectId || row.project_id === projectId)
    .map((row) => ({ ...row }));
}

async function readKeyedBlob(storeName, pictureId, memoryMap) {
  if (!pictureId) return null;
  if (storesInMemory()) return memoryMap().get(pictureId) || null;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const request = tx.objectStore(storeName).get(pictureId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export function readLocalPictureBlob(pictureId) {
  return readKeyedBlob(BLOBS, pictureId, () => memoryDb().blobs);
}

export function readLocalPictureThumb(pictureId) {
  return readKeyedBlob(THUMBS, pictureId, () => memoryDb().thumbs);
}

export async function deleteLocalPicture(pictureId, { keepNotes = false } = {}) {
  if (!pictureId) return;
  const noteIds = keepNotes
    ? []
    : (await readAllNotes())
      .filter((note) => note.picture_id === pictureId)
      .map((note) => note.comment_id);
  if (storesInMemory()) {
    const db = memoryDb();
    db.meta.delete(pictureId);
    db.blobs.delete(pictureId);
    db.thumbs.delete(pictureId);
    noteIds.forEach((id) => db.notes.delete(id));
  } else {
    const db = await openDb();
    const tx = db.transaction([META, BLOBS, THUMBS, NOTES], "readwrite");
    tx.objectStore(META).delete(pictureId);
    tx.objectStore(BLOBS).delete(pictureId);
    tx.objectStore(THUMBS).delete(pictureId);
    noteIds.forEach((id) => tx.objectStore(NOTES).delete(id));
    await transactionDone(tx);
  }
  notify({ type: "settled", pictureId });
}

async function readAllNotes() {
  if (storesInMemory()) return [...memoryDb().notes.values()];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(NOTES, "readonly");
    const request = tx.objectStore(NOTES).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function putLocalPictureNote(note) {
  const record = { ...note };
  if (storesInMemory()) {
    memoryDb().notes.set(record.comment_id, record);
  } else {
    const db = await openDb();
    const tx = db.transaction(NOTES, "readwrite");
    tx.objectStore(NOTES).put(record);
    await transactionDone(tx);
  }
  notify({ type: "update", pictureId: record.picture_id });
  return record;
}

export async function updateLocalPictureNote(commentId, patch) {
  const notes = await readAllNotes();
  const current = notes.find((note) => note.comment_id === commentId);
  if (!current) return null;
  return putLocalPictureNote({ ...current, ...patch, comment_id: commentId });
}

export async function listLocalPictureNotes({ organizationId, createdBy, pictureId } = {}) {
  if (!organizationId || !createdBy) return [];
  const notes = await readAllNotes();
  return notes
    .filter((note) => note.organization_id === organizationId && note.created_by === createdBy)
    .filter((note) => !pictureId || note.picture_id === pictureId)
    .map((note) => ({ ...note }));
}

export async function deleteLocalPictureNote(commentId) {
  if (!commentId) return;
  if (storesInMemory()) {
    memoryDb().notes.delete(commentId);
  } else {
    const db = await openDb();
    const tx = db.transaction(NOTES, "readwrite");
    tx.objectStore(NOTES).delete(commentId);
    await transactionDone(tx);
  }
  notify({ type: "update" });
}

function cacheKey(userId, id) {
  return `${userId}:${id}`;
}

async function readAllLibrary() {
  if (storesInMemory()) return [...memoryDb().library.values()];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LIBRARY, "readonly");
    const request = tx.objectStore(LIBRARY).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

async function readAllCommentCache() {
  if (storesInMemory()) return [...memoryDb().commentCache.values()];
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(COMMENT_CACHE, "readonly");
    const request = tx.objectStore(COMMENT_CACHE).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

export async function listCachedProjectPictures({ userId, organizationId, projectId } = {}) {
  if (!userId || !organizationId || !projectId) return [];
  const rows = await readAllLibrary();
  return rows
    .filter((row) => row.cached_for === userId && row.organization_id === organizationId && row.project_id === projectId)
    .filter((row) => row.is_active !== false)
    .map((row) => ({ ...row }));
}

export async function readCachedPictureThumb(userId, pictureId) {
  if (!userId || !pictureId) return null;
  const key = cacheKey(userId, pictureId);
  if (storesInMemory()) return memoryDb().libraryThumbs.get(key) || null;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(LIBRARY_THUMBS, "readonly");
    const request = tx.objectStore(LIBRARY_THUMBS).get(key);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function replaceCachedProjectPictures({
  userId,
  organizationId,
  projectId,
  pictures,
  thumbsById,
} = {}) {
  if (!userId || !organizationId || !projectId) return [];
  const existing = await listCachedProjectPictures({ userId, organizationId, projectId });
  const records = (pictures || []).filter((row) => row?.picture_id).map((row) => ({
    cache_key: cacheKey(userId, row.picture_id),
    picture_id: row.picture_id,
    project_id: projectId,
    location_id: row.location_id || null,
    organization_id: organizationId,
    cached_for: userId,
    created_by: row.created_by || null,
    author_name: row.author_name || "",
    captured_at: row.captured_at || null,
    created_at: row.created_at || null,
    is_active: row.is_active !== false,
    thumbnail_path: row.thumbnail_path || "",
    content_type: row.content_type || "",
    file_name: row.file_name || "",
    original_cached: false,
  }));
  const keep = new Set(records.map((row) => row.picture_id));
  const removed = existing.filter((row) => !keep.has(row.picture_id));

  if (storesInMemory()) {
    const db = memoryDb();
    removed.forEach((row) => {
      db.library.delete(row.cache_key);
      db.libraryThumbs.delete(row.cache_key);
    });
    records.forEach((row) => db.library.set(row.cache_key, row));
    Object.entries(thumbsById || {}).forEach(([pictureId, blob]) => {
      if (blob) db.libraryThumbs.set(cacheKey(userId, pictureId), blob);
    });
  } else {
    const db = await openDb();
    const tx = db.transaction([LIBRARY, LIBRARY_THUMBS], "readwrite");
    removed.forEach((row) => {
      tx.objectStore(LIBRARY).delete(row.cache_key);
      tx.objectStore(LIBRARY_THUMBS).delete(row.cache_key);
    });
    records.forEach((row) => tx.objectStore(LIBRARY).put(row));
    Object.entries(thumbsById || {}).forEach(([pictureId, blob]) => {
      if (blob) tx.objectStore(LIBRARY_THUMBS).put(blob, cacheKey(userId, pictureId));
    });
    await transactionDone(tx);
  }
  notify({ type: "library", projectId });
  return records;
}

export async function listCachedPictureComments({ userId, organizationId, pictureId } = {}) {
  if (!userId || !organizationId || !pictureId) return [];
  const rows = await readAllCommentCache();
  return rows
    .filter((row) => row.cached_for === userId && row.organization_id === organizationId && row.picture_id === pictureId)
    .filter((row) => row.is_active !== false)
    .map((row) => ({ ...row, pending_sync: false }));
}

export async function replaceCachedProjectComments({
  userId,
  organizationId,
  projectId,
  comments,
} = {}) {
  if (!userId || !organizationId || !projectId) return [];
  const existing = (await readAllCommentCache()).filter(
    (row) => row.cached_for === userId && row.organization_id === organizationId && row.project_id === projectId,
  );
  const records = (comments || []).filter((row) => row?.comment_id && row?.picture_id).map((row) => ({
    cache_key: cacheKey(userId, row.comment_id),
    comment_id: row.comment_id,
    picture_id: row.picture_id,
    project_id: projectId,
    organization_id: organizationId,
    cached_for: userId,
    body: row.body || "",
    source: row.source === "voice" ? "voice" : "typed",
    created_by: row.created_by || null,
    author_name: row.author_name || "",
    created_at: row.created_at || null,
    is_active: row.is_active !== false,
  }));
  if (storesInMemory()) {
    const db = memoryDb();
    existing.forEach((row) => db.commentCache.delete(row.cache_key));
    records.forEach((row) => db.commentCache.set(row.cache_key, row));
  } else {
    const db = await openDb();
    const tx = db.transaction(COMMENT_CACHE, "readwrite");
    existing.forEach((row) => tx.objectStore(COMMENT_CACHE).delete(row.cache_key));
    records.forEach((row) => tx.objectStore(COMMENT_CACHE).put(row));
    await transactionDone(tx);
  }
  notify({ type: "library", projectId });
  return records;
}

export function __resetPictureSyncStoreForTests() {
  forceMemory = true;
  memory = emptyMemory();
  putFailure = null;
  draining = false;
  dbPromise = null;
}

export function __failNextPicturePersistForTests(error) {
  forceMemory = true;
  putFailure = error || Object.assign(new Error("quota"), { name: "QuotaExceededError" });
}

if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window.__cpPictureSync = {
    putLocalPicture,
    listLocalPictures,
    readLocalPictureBlob,
    readLocalPictureThumb,
    deleteLocalPicture,
  };
}
